import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { jsonFetch } from '@/lib/api/jsonFetch'
import type {
  AcceptSuggestedResponse,
  VendorPricelistIntakeSummary,
  IntakeApplyResponse,
  IntakeDetailResponse,
  IntakeListResponse,
  IntakeUploadResponse,
  VendorDetailResponse,
  VendorItem,
  VendorItemCreate,
  VendorItemPatch,
  VendorItemsResponse,
  VendorListResponse,
} from './types'

/**
 * Vendor Pricelists data hooks. Mirrors `customer-pricelists/api/queries.ts`
 * — same MasterDetail shape, same EditableGrid wiring on the detail
 * panel, just pointed at the supplierinfo-backed route family at
 * `/api/vendor-pricelists/*`.
 */

const ROOT = '/api/vendor-pricelists'

export function useVendorPricelists(q: string = '') {
  return useQuery({
    queryKey: ['vendor-pricelists', 'list', q],
    queryFn: () => {
      const params = q.trim() ? `?q=${encodeURIComponent(q.trim())}` : ''
      return jsonFetch<VendorListResponse>(`${ROOT}${params}`)
    },
    staleTime: 30_000,
  })
}

/** Header + items for a single vendor (used when navigating via URL). */
export function useVendorPricelistDetail(vendorId: number | null) {
  return useQuery({
    queryKey: ['vendor-pricelists', 'detail', vendorId],
    queryFn: () => jsonFetch<VendorDetailResponse>(`${ROOT}/${vendorId}`),
    enabled: vendorId !== null,
    staleTime: 15_000,
  })
}

/** Items-only load for the EditableGrid. Separate from detail so the
 *  grid can invalidate / refetch without reloading the header. */
export function useVendorPricelistItems(vendorId: number | null) {
  return useQuery({
    queryKey: ['vendor-pricelists', 'items', vendorId],
    queryFn: () => jsonFetch<VendorItemsResponse>(`${ROOT}/${vendorId}/items`),
    enabled: vendorId !== null,
    staleTime: 15_000,
  })
}

export interface CreateVendorItemVars {
  vendorId: number
  item: VendorItemCreate
}

export function useCreateVendorItem() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: CreateVendorItemVars) =>
      jsonFetch<VendorItem>(`${ROOT}/${vars.vendorId}/items`, {
        method: 'POST',
        body: vars.item,
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({
        queryKey: ['vendor-pricelists', 'items', vars.vendorId],
      })
      void qc.invalidateQueries({ queryKey: ['vendor-pricelists', 'list'] })
    },
  })
}

export interface PatchVendorItemVars {
  itemId: number
  vendorId: number
  patch: VendorItemPatch
}

export function usePatchVendorItem() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: PatchVendorItemVars) =>
      jsonFetch<VendorItem>(`${ROOT}/items/${vars.itemId}`, {
        method: 'PATCH',
        body: vars.patch,
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({
        queryKey: ['vendor-pricelists', 'items', vars.vendorId],
      })
    },
  })
}

export interface DeleteVendorItemVars {
  itemId: number
  vendorId: number
}

export function useDeleteVendorItem() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: DeleteVendorItemVars) =>
      jsonFetch<{ ok: true; id: number }>(`${ROOT}/items/${vars.itemId}`, {
        method: 'DELETE',
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({
        queryKey: ['vendor-pricelists', 'items', vars.vendorId],
      })
    },
  })
}

// ─── Vendor Price Sheet Intake (Sprint 38) ───────────────────────────

const INTAKE_ROOT = '/api/vendor-pricelist-intake'
const API_BASE = import.meta.env.VITE_API_BASE ?? ''

/** CSV of matched products whose price changed, with % change. */
export function priceChangesCsvUrl(intakeId: string): string {
  return `${API_BASE}${INTAKE_ROOT}/intake/${intakeId}/price-changes.csv`
}

export function useVendorPriceSheetIntakes(vendorId: number | null) {
  return useQuery({
    queryKey: ['vendor-pricelists', 'intakes', vendorId],
    queryFn: () => jsonFetch<IntakeListResponse>(`${INTAKE_ROOT}/${vendorId}`),
    enabled: vendorId !== null,
    staleTime: 5_000,
  })
}

const IN_FLIGHT_STATUSES = new Set(['pending_extraction', 'extraction_running'])

export function useIntakeDetail(intakeId: string | null) {
  return useQuery({
    queryKey: ['vendor-pricelists', 'intake', intakeId],
    queryFn: () => jsonFetch<IntakeDetailResponse>(`${INTAKE_ROOT}/intake/${intakeId}`),
    enabled: intakeId !== null,
    // Poll while extraction is in flight; stop once it lands on review/
    // applied/rejected/failed — mirrors the sales-inbox adaptive-poll intent
    // without needing a separate hook for a single-vendor drawer.
    refetchInterval: (query) => {
      const status = query.state.data?.intake.status
      return status && IN_FLIGHT_STATUSES.has(status) ? 3_000 : false
    },
  })
}

export function useUploadPriceSheet() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ vendorId, file }: { vendorId: number; file: File }) => {
      const fd = new FormData()
      fd.append('file', file)
      return jsonFetch<IntakeUploadResponse>(`${INTAKE_ROOT}/${vendorId}/upload`, {
        method: 'POST',
        rawBody: fd,
      })
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['vendor-pricelists', 'intakes', vars.vendorId] })
    },
  })
}

export function usePastePriceSheet() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ vendorId, text }: { vendorId: number; text: string }) =>
      jsonFetch<IntakeUploadResponse>(`${INTAKE_ROOT}/${vendorId}/paste`, {
        method: 'POST',
        body: { text },
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['vendor-pricelists', 'intakes', vars.vendorId] })
    },
  })
}

export interface PatchIntakeLineVars {
  intakeId: string
  lineId: number
  matched_supplierinfo_id?: number | null
  matched_product_tmpl_id?: number | null
  line_status?: string
}

export function usePatchIntakeLine() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: PatchIntakeLineVars) =>
      jsonFetch(`${INTAKE_ROOT}/intake/${vars.intakeId}/lines/${vars.lineId}`, {
        method: 'PATCH',
        body: {
          matched_supplierinfo_id: vars.matched_supplierinfo_id,
          matched_product_tmpl_id: vars.matched_product_tmpl_id,
          line_status: vars.line_status,
        },
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['vendor-pricelists', 'intake', vars.intakeId] })
    },
  })
}

/** Confirm the suggested Odoo row on lines held for a price check. */
export function useAcceptSuggestedRows() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ intakeId, lineIds }: { intakeId: string; lineIds: number[] }) =>
      jsonFetch<AcceptSuggestedResponse>(
        `${INTAKE_ROOT}/intake/${intakeId}/lines/accept-suggested`,
        {
          method: 'POST',
          body: { line_ids: lineIds },
        },
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['vendor-pricelists', 'intake', vars.intakeId] })
    },
  })
}

/** End (date_end in Odoo), keep, or reopen a tier the new sheet no longer lists. */
export function useResolveDroppedRow() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { intakeId: string; rowId: number; action: 'end' | 'keep' | 'reopen' }) =>
      jsonFetch<VendorPricelistIntakeSummary>(
        `${INTAKE_ROOT}/intake/${vars.intakeId}/dropped/${vars.rowId}`,
        { method: 'POST', body: { action: vars.action } },
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['vendor-pricelists', 'intake', vars.intakeId] })
    },
  })
}

/** Recheck which Odoo tiers the sheet no longer lists. */
export function useRefreshDroppedRows() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ intakeId }: { intakeId: string }) =>
      jsonFetch<VendorPricelistIntakeSummary>(`${INTAKE_ROOT}/intake/${intakeId}/dropped/refresh`, {
        method: 'POST',
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['vendor-pricelists', 'intake', vars.intakeId] })
    },
  })
}

export function useSetIntakeEffectiveDate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ intakeId, effectiveDate }: { intakeId: string; effectiveDate: string | null }) =>
      jsonFetch<{ rescheduled?: number }>(`${INTAKE_ROOT}/intake/${intakeId}`, {
        method: 'PATCH',
        body: { effective_date: effectiveDate },
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['vendor-pricelists', 'intake', vars.intakeId] })
      void qc.invalidateQueries({ queryKey: ['vendor-price-changes', 'upcoming'] })
    },
  })
}

/** Pull scheduled lines back to review so they don't go live on their date. */
export function useUnscheduleLines() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ intakeId, lineIds }: { intakeId: string; lineIds: number[] }) =>
      jsonFetch<{ unscheduled: number[] }>(`${INTAKE_ROOT}/intake/${intakeId}/lines/unschedule`, {
        method: 'POST',
        body: { line_ids: lineIds },
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['vendor-pricelists', 'intake', vars.intakeId] })
      void qc.invalidateQueries({ queryKey: ['vendor-pricelists', 'intakes'] })
      void qc.invalidateQueries({ queryKey: ['vendor-price-changes', 'upcoming'] })
    },
  })
}

export function useSetIntakePriceUnit() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ intakeId, unit }: { intakeId: string; unit: 'kg' | 'lb' }) =>
      jsonFetch(`${INTAKE_ROOT}/intake/${intakeId}/price-unit`, {
        method: 'POST',
        body: { unit },
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['vendor-pricelists', 'intake', vars.intakeId] })
    },
  })
}

export function useApplyIntake() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ intakeId, lineIds }: { intakeId: string; lineIds: number[]; vendorId: number }) =>
      jsonFetch<IntakeApplyResponse>(`${INTAKE_ROOT}/intake/${intakeId}/apply`, {
        method: 'POST',
        body: { line_ids: lineIds },
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['vendor-pricelists', 'intake', vars.intakeId] })
      void qc.invalidateQueries({ queryKey: ['vendor-pricelists', 'intakes', vars.vendorId] })
      void qc.invalidateQueries({ queryKey: ['vendor-pricelists', 'items', vars.vendorId] })
    },
  })
}

export function useRejectIntake() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ intakeId, reason }: { intakeId: string; reason: string; vendorId: number }) =>
      jsonFetch(`${INTAKE_ROOT}/intake/${intakeId}/reject`, {
        method: 'POST',
        body: { reason },
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['vendor-pricelists', 'intake', vars.intakeId] })
      void qc.invalidateQueries({ queryKey: ['vendor-pricelists', 'intakes', vars.vendorId] })
    },
  })
}

export function useRetryIntake() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ intakeId }: { intakeId: string; vendorId: number }) =>
      jsonFetch(`${INTAKE_ROOT}/intake/${intakeId}/retry`, { method: 'POST' }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['vendor-pricelists', 'intake', vars.intakeId] })
      void qc.invalidateQueries({ queryKey: ['vendor-pricelists', 'intakes', vars.vendorId] })
    },
  })
}
